import React, { useCallback, useEffect, useRef, useState } from "react";
import DatePicker, { CalendarContainer } from "react-datepicker";
import { getYear, getMonth, format } from "date-fns";
import VideoBorder from "../../assets/images/borders/video-border.webp";
import useOnclickOutside from "react-cool-onclickoutside";
import type { ICustomDatePickerInterface } from "@src/types/AppTypes";

const CustomDatePicker: React.FC<ICustomDatePickerInterface> = ({
  value: selectedDateValue,
  setDob,
}) => {
  const pickerRef = useRef(null);
  const [isYear, setIsYear] = useState<number>(getYear(new Date()));
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState<boolean>(false);
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] =
    useState<boolean>(false);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  const yearDropdownRef = useOnclickOutside(
    () => {
      setIsYearDropdownOpen(false);
    },
    { ignoreClass: ["swal2-container"] }
  );
  const monthDropdownRef = useOnclickOutside(
    () => {
      setIsMonthDropdownOpen(false);
    },
    { ignoreClass: ["swal2-container"] }
  );

  // for select how many years we need to display in years dropdown in datepicker
  const range = useCallback((start: number, end: number, step = 1) => {
    const output = [];
    for (let i = start; i <= end; i += step) {
      output.push(i);
    }
    return output;
  }, []);

  // custom years list
  const years = range(1950, getYear(new Date()), 1);
  // custom months list
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // for doing overflow auto on page leave
  useEffect(() => {
    const signupModal = document.getElementById("signupModal");

    return () => {
      signupModal!.style.overflow = "auto";
    };
  }, []);

  // for prevent background scroll when react-datepicker open
  const handleCalendarOpen = useCallback(() => {
    const signupModal = document.getElementById("signupModal");
    if (signupModal) {
      signupModal.style.overflow = "hidden";
    }
  }, []);

  // for set scroll auto when react-datepicker close
  const handleCalendarClose = useCallback(() => {
    const signupModal = document.getElementById("signupModal");
    if (signupModal) {
      signupModal.style.overflow = "auto";
    }
  }, []);

  const monthsWithDisable = months.map((month, index) => {
    const year = isYear;
    return {
      month,
      disable: year < currentYear ? false : index > currentMonth,
    };
  });

  useEffect(() => {
    import("react-datepicker/dist/react-datepicker.css");
  }, []);

  // for keep into view selected year
  useEffect(() => {
    const selectedYears = document.getElementsByClassName("bg-opacity-20");
    for (const i of selectedYears) {
      (i as HTMLLIElement).scrollIntoView({
        block: "center",
        inline: "center",
        behavior: "instant",
      });
    }
  }, [isYearDropdownOpen]);

  return (
    <div className="relative">
      <DatePicker
        ref={pickerRef}
        className={`bg-primary w-full text-base max-md:text-sm p-[1.6rem] max-md:px-4 max-md:py-5 rounded-[5px_27px_19px_25px] bg-100 bg-no-repeat bg-[url('@assets/images/borders/inputBorder.webp')] f-family-mochiy-pop`}
        calendarContainer={CustomContainer}
        dateFormat={"MM/dd/yy"}
        customInput={
          <div
            className={`min-h-[75px] h-[75px] w-full flex items-center ${
              selectedDateValue ? "!text-black" : "!text-gray-400"
            }`}
          >
            {selectedDateValue
              ? format(selectedDateValue, "MM/dd/yyyy")
              : "MM/DD/YYYY"}
          </div>
        }
        onSelect={(date: Date | null) => {
          if (date) setDob(date);
        }}
        onCalendarOpen={() => {
          handleCalendarOpen();
        }}
        onCalendarClose={() => {
          handleCalendarClose();
        }}
        maxDate={new Date()}
        renderCustomHeader={({
          date,
          changeYear,
          changeMonth,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <div className="flex justify-between mx-7 my-4 max-sm:mx-3 max-sm:gap-2">
            <div className="flex items-center gap-4 max-sm:gap-2">
              {/* month dropdown */}
              <button
                className="relative flex items-center"
                onClick={(e) => {
                  e.preventDefault();
                  setIsMonthDropdownOpen(!isMonthDropdownOpen);
                }}
              >
                <input
                  type="text"
                  className="bg-transparent text-primary font-semibold w-[105px] max-sm:w-[90px] text-base cursor-pointer max-sm:text-sm"
                  value={months[getMonth(date)]}
                  readOnly
                />
                {isMonthDropdownOpen ? (
                  <i className="ri-arrow-up-s-fill text-primary text-2xl pointer-events-none"></i>
                ) : (
                  <i className="ri-arrow-down-s-fill text-primary text-2xl pointer-events-none"></i>
                )}
                {isMonthDropdownOpen && (
                  <ul
                    ref={monthDropdownRef}
                    className="p-1 h-48 rounded-lg bg-primary overflow-y-auto month-dropdown absolute top-10 z-[99] w-full"
                  >
                    {monthsWithDisable.map((months) => {
                      return (
                        <li
                          key={months.month}
                          className={`px-2 py-1 hover:bg-black font-semibold text-[12px] hover:bg-opacity-20 ${
                            months.month ===
                            monthsWithDisable[getMonth(date)].month
                              ? "bg-black bg-opacity-20"
                              : ""
                          }`}
                          onClick={() => {
                            if (!months.disable) {
                              changeMonth(
                                monthsWithDisable.findIndex(
                                  (item) => item.month === months.month
                                )
                              );
                              selectedDateValue?.setMonth(
                                monthsWithDisable.findIndex(
                                  (item) => item.month === months.month
                                )
                              );

                              setDob(selectedDateValue);
                              setIsMonthDropdownOpen(false);
                            }
                          }}
                          role="button"
                          aria-label={months.month}
                          style={{
                            cursor: months.disable ? "not-allowed" : "pointer",
                            opacity: months.disable ? 0.5 : 1,
                          }}
                        >
                          {months.month}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </button>

              {/* year dropdown */}
              <div className="relative flex items-center">
                <input
                  type="text"
                  className="bg-transparent text-primary font-semibold w-[50px] text-base cursor-pointer max-sm:text-sm"
                  value={getYear(date)}
                  onClick={(e) => {
                    e.preventDefault();
                    setIsYearDropdownOpen(!isYearDropdownOpen);
                  }}
                  readOnly
                />
                {isYearDropdownOpen ? (
                  <i className="ri-arrow-up-s-fill text-primary text-2xl pointer-events-none"></i>
                ) : (
                  <i className="ri-arrow-down-s-fill text-primary text-2xl pointer-events-none"></i>
                )}
                {isYearDropdownOpen && (
                  <ul
                    ref={yearDropdownRef}
                    className="p-1 h-48 rounded-lg bg-primary overflow-y-auto month-dropdown absolute top-10 z-[99] w-full"
                  >
                    {years.map((year) => {
                      return (
                        <li
                          key={year}
                          className={`px-2 py-1  hover:bg-black font-semibold hover:bg-opacity-20 ${
                            year === getYear(date)
                              ? "bg-black bg-opacity-20"
                              : ""
                          }`}
                          onClick={(e) => {
                            e.preventDefault();

                            setIsYear(year);
                            changeYear(Number(year));
                            selectedDateValue?.setFullYear(Number(year));
                            if (
                              year === new Date().getFullYear() &&
                              getMonth(date) > new Date().getMonth()
                            ) {
                              changeMonth(new Date().getMonth());
                              setDob(new Date());
                            } else {
                              setDob(selectedDateValue);
                            }

                            setIsYearDropdownOpen(false);
                          }}
                          role="button"
                          aria-label={year.toString()}
                        >
                          {year}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

            {/* prev month and next month button */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={decreaseMonth}
                className="text-white cursor-pointer w-7 h-7 disabled:opacity-50"
                disabled={prevMonthButtonDisabled}
                aria-label="Left arrow"
              >
                <i className="ri-arrow-left-s-line text-xl text-primary font-bold"></i>
              </button>
              <button
                type="button"
                onClick={increaseMonth}
                className="text-white cursor-pointer w-7 h-7 disabled:opacity-50"
                disabled={nextMonthButtonDisabled}
                aria-label="Right arrow"
              >
                <i className="ri-arrow-right-s-line text-xl text-primary font-bold"></i>
              </button>
            </div>
          </div>
        )}
        selected={selectedDateValue || new Date()}
        placeholderText="MM/DD/YYYY"
        onChange={(date: Date | null) => {
          if (date) {
            setDob(date);
          }
        }}
        onChangeRaw={(e) => {
          e?.preventDefault();
          e?.stopPropagation();
        }}
      />
      <button className="absolute right-6 top-[50%] -translate-y-2/4 pointer-events-none z-[14]">
        <i className="ri-calendar-line text-xl"></i>
      </button>
    </div>
  );
};

const CustomContainer: React.FC<{
  className: string;
  children: React.ReactNode;
}> = ({ className, children }) => {
  return (
    <div className="custom-calendar-container">
      <div>
        <img className="image" src={VideoBorder} />
      </div>
      <CalendarContainer className={className}>
        <div className="custom-container">{children}</div>
      </CalendarContainer>
    </div>
  );
};

export default CustomDatePicker;
